import { opaqueId } from './groupTripModel';
import { travelerPresentation } from './disruptionModel';
import {
  CONSEQUENCE_TYPES, consequenceTypeLabel, DIRECTIONS, directionLabel, emptyConsequenceDraft, isMonetaryDirection,
} from './settlementModel';

const MAX_ROWS = 50;

function beneficiaryLabel(beneficiary, travelersById) {
  if (beneficiary?.travelerId != null) return travelerPresentation(beneficiary.travelerId, travelersById).primary;
  return beneficiary?.externalDisplayName || '受益人';
}

/**
 * 協議項目編輯器：金額項目的當事人固定為訂位付款人（由後端解析），放棄權益需選擇受益人。
 * 不提供均分、按人數或座位分攤，也不從既有付款金額推算任何數字。
 */
export default function SettlementConsequenceEditor({
  idPrefix, drafts, onChange, errors, beneficiaries, travelersById, disabled,
}) {
  const update = (index, patch) => onChange(drafts.map((draft, position) => (position === index ? { ...draft, ...patch } : draft)));
  const remove = (index) => onChange(drafts.filter((_, position) => position !== index));
  const rows = errors?.rows || [];

  return (
    <div className="settlement__editor">
      <p className="settlement__hint">
        金額項目的當事人為此訂位的付款人；多人金額分攤目前尚未支援。放棄權益項目請選擇對應的受益人。
      </p>
      {drafts.map((draft, index) => {
        const base = `${idPrefix}-${index}`;
        const problems = rows[index] || {};
        const monetary = isMonetaryDirection(draft.direction);
        const field = (name) => ({
          id: `${base}-${name}`,
          'aria-invalid': problems[name] ? 'true' : undefined,
          'aria-describedby': problems[name] ? `${base}-${name}-error` : undefined,
        });
        const problem = (name) => (problems[name]
          ? <span className="settlement__field-error" id={`${base}-${name}-error`}>{problems[name]}</span> : null);
        return (
          <fieldset className="settlement__row" key={base} disabled={disabled}>
            <legend>協議項目 {index + 1}</legend>
            <label htmlFor={`${base}-type`}>項目類型
              <select {...field('type')} value={draft.type} onChange={(event) => update(index, { type: event.target.value })}>
                {CONSEQUENCE_TYPES.map((value) => <option key={value} value={value}>{consequenceTypeLabel(value)}</option>)}
              </select>
            </label>
            {problem('type')}
            <label htmlFor={`${base}-direction`}>協議方向
              <select {...field('direction')} value={draft.direction} onChange={(event) => update(index, { direction: event.target.value })}>
                {DIRECTIONS.map((value) => <option key={value} value={value}>{directionLabel(value)}</option>)}
              </select>
            </label>
            {problem('direction')}
            {monetary ? (
              <>
                <p className="settlement__secondary">相關當事人：訂位付款人（由系統依訂位資料確認）</p>
                <label htmlFor={`${base}-amount`}>金額
                  <input {...field('amount')} inputMode="decimal" autoComplete="off" value={draft.amount}
                    onChange={(event) => update(index, { amount: event.target.value })} />
                </label>
                {problem('amount')}
                <label htmlFor={`${base}-currency`}>幣別代碼
                  <input {...field('currency')} maxLength={16} autoComplete="off" placeholder="例如 TWD" value={draft.currency}
                    onChange={(event) => update(index, { currency: event.target.value })} />
                </label>
                {problem('currency')}
              </>
            ) : (
              <>
                <label htmlFor={`${base}-beneficiaryId`}>放棄權益的受益人
                  <select {...field('beneficiaryId')} value={draft.beneficiaryId}
                    onChange={(event) => update(index, { beneficiaryId: event.target.value })}>
                    <option value="">請選擇</option>
                    {(beneficiaries || []).map((beneficiary) => {
                      const id = opaqueId(beneficiary?.beneficiaryId);
                      return id ? <option key={id} value={id}>{beneficiaryLabel(beneficiary, travelersById)}</option> : null;
                    })}
                  </select>
                </label>
                {problem('beneficiaryId')}
              </>
            )}
            <label htmlFor={`${base}-description`}>{draft.type === 'OTHER' ? '說明（必填）' : '說明'}
              <textarea {...field('description')} maxLength={500} value={draft.description}
                onChange={(event) => update(index, { description: event.target.value })} />
            </label>
            {problem('description')}
            <label htmlFor={`${base}-evidenceReference`}>{monetary ? '依據或參考編號（必填）' : '依據或參考編號'}
              <input {...field('evidenceReference')} maxLength={255} value={draft.evidenceReference}
                onChange={(event) => update(index, { evidenceReference: event.target.value })} />
            </label>
            {problem('evidenceReference')}
            {drafts.length > 1 ? (
              <button type="button" className="settlement__button settlement__button--secondary" onClick={() => remove(index)}>
                移除協議項目 {index + 1}
              </button>
            ) : null}
          </fieldset>
        );
      })}
      {errors?.form ? <p className="settlement__field-error" role="alert">{errors.form}</p> : null}
      {drafts.length < MAX_ROWS ? (
        <button type="button" className="settlement__button settlement__button--secondary" disabled={disabled}
          onClick={() => onChange([...drafts, emptyConsequenceDraft()])}>
          新增協議項目
        </button>
      ) : null}
    </div>
  );
}
